import {
  formatAuthorTime,
  formatSpeedMph,
  parseAuthorTime,
  type ScenarioClockConfig,
} from './format'
import { storyAt, storyDeltaMs } from './storyTime'
import type { DisplayUnits, WindKeyframe } from './types'

export type WindPresetId = 'steady_northerly' | 'west_stormfront' | 'random'

export type WindPresetDef = {
  id: WindPresetId
  /** Short menu label */
  label: string
  /** Preferred wall-clock onset when the board has script/offset time */
  preferredOnsetHm?: string
  defaultSpanMs: number
}

export const WIND_PRESET_SPAN_OPTIONS: { ms: number; label: string }[] = [
  { ms: 60 * 60_000, label: '1 hour' },
  { ms: 2 * 60 * 60_000, label: '2 hours' },
  { ms: 4 * 60 * 60_000, label: '4 hours' },
]

export const WIND_PRESETS: readonly WindPresetDef[] = [
  {
    id: 'steady_northerly',
    label: 'Steady northerly',
    preferredOnsetHm: '12:00 PM',
    defaultSpanMs: 4 * 60 * 60_000,
  },
  {
    id: 'west_stormfront',
    label: 'West stormfront',
    preferredOnsetHm: '10:00 AM',
    defaultSpanMs: 2 * 60 * 60_000,
  },
  {
    id: 'random',
    label: 'Random weather',
    defaultSpanMs: 2 * 60 * 60_000,
  },
]

/** Authoring caps for surface-wind dials (extreme weather / future tornado track). */
export const WIND_SPEED_SLIDER_MAX_MPH = 150
export const WIND_PLUME_DISTANCE_SLIDER_MAX_M = 5000

export function windPresetById(id: WindPresetId): WindPresetDef {
  return WIND_PRESETS.find((p) => p.id === id) ?? WIND_PRESETS[0]!
}

/** Prefer a wall-clock onset when the board has one; otherwise keep the current author playhead. */
export function suggestedWindPresetOnsetMs(
  preset: WindPresetDef,
  clock: ScenarioClockConfig,
  fallbackMs: number,
): number {
  if (preset.preferredOnsetHm) {
    const iso = parseAuthorTime(preset.preferredOnsetHm, clock)
    if (iso != null) return Math.max(0, storyDeltaMs(clock.startsAt, iso))
  }
  return Math.max(0, Math.round(fallbackMs))
}

/** One-line story — speeds/distances and clock phrases follow board Settings. */
export function formatWindPresetBlurb(
  id: WindPresetId,
  opts: {
    displayUnits: DisplayUnits
    clock: ScenarioClockConfig
    onsetMs: number
    spanMs: number
  },
): string {
  const start = formatAuthorTime(storyAt(opts.clock.startsAt, opts.onsetMs), opts.clock)
  const end = formatAuthorTime(
    storyAt(opts.clock.startsAt, opts.onsetMs + Math.max(0, opts.spanMs)),
    opts.clock,
  )
  const window = `${start} → ${end}`
  if (id === 'steady_northerly') {
    return `Steady north wind ${window}, building after the midpoint.`
  }
  if (id === 'west_stormfront') {
    const peak = formatSpeedMph(100, opts.displayUnits)
    return `West front ${window}; peak gusts near three-quarters of the span (up to ~${peak}).`
  }
  return `Surprise shifts in direction and speed across ${window} — same onset+duration rebuilds the same pattern.`
}

function clampDeg(deg: number): number {
  return ((Math.round(deg) % 360) + 360) % 360
}

function frame(
  originIso: string,
  atElapsedMs: number,
  fromDeg: number,
  speedMph: number,
  plumeDistanceM: number,
  plumeSpreadDeg: number,
): WindKeyframe {
  return {
    at: storyAt(originIso, Math.max(0, Math.round(atElapsedMs))),
    fromDeg: clampDeg(fromDeg),
    speedMph: Math.max(0, Math.round(speedMph)),
    plumeDistanceM: Math.max(50, Math.round(plumeDistanceM)),
    plumeSpreadDeg: Math.max(5, Math.min(90, Math.round(plumeSpreadDeg))),
  }
}

function atSpan(onsetMs: number, spanMs: number, u: number): number {
  return onsetMs + Math.round(Math.max(0, Math.min(1, u)) * Math.max(1, spanMs))
}

/** Deterministic 0..1 from a seed (reproducible “random” weather). */
function mulberry32(seed: number): () => number {
  let t = seed >>> 0
  return () => {
    t += 0x6d2b79f5
    let r = Math.imul(t ^ (t >>> 15), 1 | t)
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r)
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Keep preset onset+duration inside the scenario clock so every generated
 * mark is reachable by scrubbing (and by Preview).
 * Prefer preserving duration: shift onset earlier when the window would run past the end.
 * If the clock is shorter than the requested span, shrink the span to fit.
 */
export function clampWindPresetWindow(
  onsetMs: number,
  spanMs: number,
  clockEndMs: number,
): { onsetMs: number; spanMs: number } {
  const end = Math.max(1, Math.round(clockEndMs))
  const minSpan = 5 * 60_000
  let span = Math.max(minSpan, Math.round(spanMs))
  if (span > end) span = end
  let onset = Math.round(onsetMs)
  if (!Number.isFinite(onset) || onset < 0) onset = 0
  if (onset > end) onset = Math.max(0, end - span)
  if (onset + span > end) {
    onset = Math.max(0, end - span)
    span = Math.max(minSpan, Math.min(span, end - onset))
  }
  return { onsetMs: onset, spanMs: span }
}

/**
 * Build a short keyframe series for a weather preset.
 * `onsetMs` is the first mark; `spanMs` is first→last.
 * Callers should clamp via `clampWindPresetWindow` first when a clock end is known.
 */
export function buildWindPresetFrames(
  id: WindPresetId,
  opts: { onsetMs: number; spanMs: number; originIso: string },
): WindKeyframe[] {
  const onset = Math.max(0, Math.round(opts.onsetMs))
  const span = Math.max(5 * 60_000, Math.round(opts.spanMs))
  const t = (u: number) => atSpan(onset, span, u)
  const mark = (
    ms: number,
    fromDeg: number,
    speedMph: number,
    plumeDistanceM: number,
    plumeSpreadDeg: number,
  ) => frame(opts.originIso, ms, fromDeg, speedMph, plumeDistanceM, plumeSpreadDeg)

  if (id === 'steady_northerly') {
    return [
      mark(t(0), 0, 10, 320, 18),
      mark(t(0.3), 5, 16, 400, 22),
      mark(t(0.5), 0, 22, 480, 26),
      mark(t(0.65), 355, 30, 620, 30),
      mark(t(0.85), 0, 36, 720, 34),
      mark(t(1), 2, 32, 680, 30),
    ]
  }

  if (id === 'west_stormfront') {
    return [
      mark(t(0), 250, 12, 350, 20),
      mark(t(0.25), 270, 38, 550, 28),
      mark(t(0.45), 275, 62, 780, 36),
      mark(t(0.75), 280, 100, 1100, 48),
      mark(t(0.9), 255, 48, 700, 32),
      mark(t(1), 240, 20, 420, 22),
    ]
  }

  const rand = mulberry32((onset * 2654435761) ^ (span * 1597334677))
  const count = 6
  const frames: WindKeyframe[] = []
  let deg = rand() * 360
  for (let i = 0; i < count; i++) {
    const u = i / (count - 1)
    deg = clampDeg(deg + (rand() - 0.45) * 140)
    const speed = 6 + rand() * (rand() > 0.82 ? 110 : 45)
    const dist = 250 + rand() * 1200
    const spread = 14 + rand() * 40
    frames.push(mark(t(u), deg, speed, dist, spread))
  }
  return frames
}
